import { GoalStatus, type Home } from '@lmc/ts-common'
import {
  FormItem,
  GroupedResidentsMultiSelect,
  RangePicker,
  Select
} from '@lmc/ui-kit'
import { getNominative } from 'common/utils'
import useResidentsList from 'hooks/useResidentsList'
import { NAMESPACE } from 'locale'
import { goalReportsConstants } from 'locale/goalReports'
import { reportingTranslations } from 'locale/reporting'
import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

type Props = {
  preferredPeopleNominative: string
  isGroupDownload?: boolean
}

export const OutcomesAndGoalsForm: FC<Props> = ({
  preferredPeopleNominative,
  isGroupDownload
}) => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const { t: tGoals } = useTranslation(NAMESPACE.GOAL_REPORTS)

  const residentsNominative = getNominative(
    preferredPeopleNominative,
    'Residents'
  )

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const homeOptions = useMemo(
    () =>
      homes
        .filter((home) => home.features?.goals?.enabled)
        .map((home) => ({
          value: home.id,
          label: home.name
        })),
    [homes]
  )

  const { residents } = useResidentsList({})

  const statusOptions = [
    {
      value: GoalStatus.inProgress,
      label: tGoals(goalReportsConstants[GoalStatus.inProgress])
    },
    {
      value: GoalStatus.paused,
      label: tGoals(goalReportsConstants[GoalStatus.paused])
    },
    {
      value: GoalStatus.notStarted,
      label: tGoals(goalReportsConstants[GoalStatus.notStarted])
    },
    {
      value: GoalStatus.done,
      label: tGoals(goalReportsConstants[GoalStatus.done])
    },
    {
      value: GoalStatus.archive,
      label: tGoals(goalReportsConstants[GoalStatus.archive])
    }
  ]

  return (
    <>
      <FormItem
        label={t(reportingTranslations.OUTCOMES_AND_GOALS_CREATED)}
        name="dateRange"
        extra={t(reportingTranslations.OUTCOMES_AND_GOALS_SELECT_DATES)}
        rules={[
          {
            required: true
          }
        ]}
        long
      >
        <RangePicker />
      </FormItem>
      <FormItem
        label={residentsNominative}
        name="residents"
        extra={t(reportingTranslations.ROTA_SERVICE_USERS_EXTRA, {
          variable: residentsNominative
        })}
      >
        <GroupedResidentsMultiSelect
          residents={residents}
          title={residentsNominative}
          // @ts-ignore
        />
      </FormItem>
      <FormItem
        label={t(reportingTranslations.OUTCOMES_AND_GOAL_STATUS)}
        name="statuses"
        extra={t(reportingTranslations.ROTA_TEAM_MEMBERS_EXTRA)}
      >
        <Select options={statusOptions} mode="multiple" />
      </FormItem>
      {isGroupDownload && (
        <FormItem
          label={t(reportingTranslations.OUTCOMES_AND_GOAL_SITES)}
          name="sites"
          extra={t(reportingTranslations.ROTA_TEAM_MEMBERS_EXTRA)}
          rules={[{ required: true }]}
        >
          <Select options={homeOptions} mode="multiple" />
        </FormItem>
      )}
    </>
  )
}
